import { FormikProps } from "formik";
import axios from "axios";
import { ValidateBVNType } from "../types/validateBvn";
import {
  channelMap,
  idCardRegexMap,
  monthNames,
  utmKeys,
  youVerifyTasks,
} from "./constants";
import {
  AvailableChannelsResponse,
  IMeansOfIdentification,
  YouVerifyErrorResponse,
  YouVerifySuccessResponse,
} from "../models/IEnums.model";
import { getLocalStorageItem } from "./localStorage";
import { errorToast, successToast } from "./makeToast";
import altBankLogo from "../assets/images/altbank_logo_black_gold.png";

export function logData(data: any) {
  const dataToString = JSON.stringify(data);
  const element = document.getElementById("log-output");
  if (element) {
    element.innerText = dataToString;
  }
}

export const handleScrollElement = (
  id: string,
  scrollDirection: "up" | "down" | "left" | "right",
  scrollValue?: number,
) => {
  const element = document.getElementById(id);

  if (element) {
    element.scrollBy({
      top:
        scrollDirection === "up"
          ? -element.scrollHeight
          : scrollDirection === "down"
            ? element.scrollHeight
            : 0,
      left:
        scrollDirection === "left"
          ? -(element.scrollWidth / (scrollValue || 1))
          : scrollDirection === "right"
            ? element.scrollWidth / (scrollValue || 1)
            : 0,
      behavior: "smooth",
    });
  }
};

export function convertFileToBase64(
  file: File | Blob,
  maxWidth = 800,
): Promise<string> {
  return new Promise((resolve, reject) => {
    // Only resize images
    if (file.type.startsWith("image/")) {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        const canvas = document.createElement("canvas");

        const scale = Math.min(1, maxWidth / img.width);

        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);

        const ctx = canvas.getContext("2d");

        if (!ctx) {
          return reject(new Error("Canvas not supported"));
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const outputDataURL = canvas.toDataURL(file.type || "image/png");
        resolve(outputDataURL);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to load image for resizing"));
      };

      // Use blob URL instead of data:image/... URL
      img.src = objectUrl;

      return;
    }

    // Non-image files still need to be converted to Base64
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== "string") {
        return reject(new Error("Failed to read file as base64"));
      }

      resolve(reader.result);
    };

    reader.onerror = (error) => {
      console.error("Error occurred while converting file to base64:", error);
      reject(error);
    };

    reader.readAsDataURL(file);
  });
}

export function removeMetadataFromFile(
  file: File,
  callback: (blob: Blob) => void,
): void {
  const reader = new FileReader();

  if (file.type.startsWith("image/")) {
    reader.onload = function (event) {
      const img = new Image();

      const imageUrl = URL.createObjectURL(file);

      img.onload = function () {
        URL.revokeObjectURL(imageUrl);

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");

        if (ctx) {
          ctx.drawImage(img, 0, 0);

          canvas.toBlob((blob) => {
            if (blob) {
              callback(blob);
            }
          }, file.type);
        }
      };

      img.src = imageUrl;
    };
    reader.readAsDataURL(file);
  } else if (file.type === "application/pdf") {
    reader.onload = function (event) {
      if (event.target?.result instanceof ArrayBuffer) {
        const cleanBlob = new Blob([new Uint8Array(event.target.result)], {
          type: "application/pdf",
        });
        callback(cleanBlob);
      } else {
        console.error("Failed to read PDF as ArrayBuffer.");
      }
    };
    reader.readAsArrayBuffer(file);
  } else {
    console.error("Unsupported file type. Only images and PDFs are supported.");
  }
}

export const isValidFileType = (file: File): boolean => {
  if (!file) return false;
  const allowedExtensions = ["jpg", "jpeg", "png", "pdf"];
  const fileExtension = file?.name
    ? file.name.split(".").pop()?.toLowerCase()
    : file.type?.split("/")?.[1]?.toLowerCase();
  return fileExtension ? allowedExtensions.includes(fileExtension) : false;
};

export const returnDateOfBirth = ({ day, month, year }: ValidateBVNType) => {
  return `${year || ""}-${monthNames[month] || ""}-${day || ""}`;
};

export const handleNumericInput = (
  e: React.KeyboardEvent<HTMLInputElement>,
  idCardType?: number,
) => {
  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
  if (allowedKeys.includes(e.key)) return;
  if (e.ctrlKey || e.metaKey) return;
  if (idCardType && idCardRegexMap[idCardType]) {
    const input = e.currentTarget.value + e.key;
    if (!idCardRegexMap[idCardType].test(input)) {
      e.preventDefault();
    }
    return;
  }
  if (!/^\d$/.test(e.key)) {
    e.preventDefault();
  }
};

export const handleNumericPaste = (
  e: React.ClipboardEvent<HTMLInputElement>,
  idCardType?: number,
) => {
  const pastedText = e.clipboardData.getData("text");

  if (idCardType && idCardRegexMap[idCardType]) {
    if (!idCardRegexMap[idCardType].test(pastedText)) {
      e.preventDefault();
    }
  } else if (!/^\d+$/.test(pastedText)) {
    e.preventDefault();
  }
};

export const returnInputErrorMessage = <T extends object>(
  formik: FormikProps<T>,
  fieldName: keyof T,
): string => {
  if (!fieldName || !formik) return "";
  return (
    formik.touched[fieldName] && ((formik.errors[fieldName] as string) || "")
  );
};

export const returnLgaError = ({
  stateError,
  lgaError,
}: {
  stateError: string;
  lgaError: string;
}) => {
  if (!lgaError && !stateError) return "";
  return stateError || lgaError;
};

export const isEmptyObj = <T>(obj: T) => {
  return JSON.stringify(obj) === "{}";
};

export const yearsList = Array.from({
  length: new Date().getFullYear() - 1939,
}).map((_, index) => `${index + 1940}`);

export const minorYearslist = Array.from({ length: 18 }).map(
  (_, index) => `${new Date().getFullYear() - 17 + index}`,
);
export const returnIdTypes = (arr: IMeansOfIdentification[]) => {
  if (!arr) return [];
  return arr.map((item) => item.text);
};

export const startCamera = async (
  videoRef: React.RefObject<HTMLVideoElement>,
  streamRef: React.MutableRefObject<MediaStream | null>,
) => {
  navigator.mediaDevices
    .getUserMedia({ video: true })
    .then((mediaStream) => {
      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    })
    .catch((err) => console.error("Camera Access Denied:", err));
};

export const stopCamera = async (
  videoRef: React.MutableRefObject<HTMLVideoElement>,
  streamRef: React.MutableRefObject<MediaStream | null>,
) => {
  if (streamRef.current) {
    streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }
  if (videoRef.current) {
    videoRef.current.srcObject = null;
  }
};

export const returnLivenessOptions = ({
  onClose,
  onFailure,
  onSuccess,
  sessionId,
  sessionToken,
}: {
  onSuccess: (data: YouVerifySuccessResponse) => void;
  onFailure: (error: YouVerifyErrorResponse) => void;
  onClose: () => void;
  sessionId: string;
  sessionToken: string;
}) => {
  const userDetails = getLocalStorageItem("userDetails") || {};
  return {
    publicKey: returnConfigVariable("REACT_APP_YOUVERIFY_PUBLIC_MERCHANT_KEY"),
    sandboxEnvironment: returnConfigVariable("REACT_APP_ENV") === "qa",
    presentation: "modal" as any,
    tasks: youVerifyTasks,
    user: {
      firstName: userDetails?.firstName,
      lastName: userDetails?.lastName,
    },
    branding: {
      name: "The Alternative Bank",
      color: "#B59745",
      logo: altBankLogo,
      logoAlt: "AltBank",
    },
    sessionToken,
    sessionId,
    onSuccess,
    onFailure,
    onClose,
  };
};

export const copyText = (text: string) => {
  if (!text) return errorToast("There is no text to copy!");
  navigator.clipboard
    .writeText(text)
    .then(() => successToast("Copied successfully!"))
    .catch(() => errorToast(`Could not copy text. Try again!`));
};

export const extractValuesFromObj = <
  T extends Record<string, any>,
  O extends Partial<Record<keyof T, any>>,
>(
  obj1: T,
  obj2: O,
): Partial<T> => {
  const newObj: Partial<T> = {};

  for (const key in obj1) {
    if (
      Object.prototype.hasOwnProperty.call(obj1, key) &&
      Object.prototype.hasOwnProperty.call(obj2, key)
    ) {
      const element = obj2[key] || obj1[key];
      (newObj[key] as keyof T[keyof T]) = element as keyof T[keyof T];
    }
  }

  return newObj;
};

export const returnAvailableChannels = (
  arr: AvailableChannelsResponse["data"]["availableOtps"],
  options: { icon: React.FC | string; label: string }[],
): { icon: React.FC | string; label: string }[] => {
  if (!arr || !options) return [];
  return options.filter(
    (option) =>
      !!arr.find(
        (item) => channelMap[option.label.split(" ")[0].toLowerCase()] === item,
      ),
  );
};

export function generateRandomFourDigitNumber(): number {
  return Math.floor(1000 + Math.random() * 9000);
}

export const handleDownload = (filePath: string, fileName: string) => {
  if (!filePath) return;
  const link = document.createElement("a");
  link.href = filePath;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const secondsBetweenDates = (dateString: string): number => {
  if (!dateString) return 0;
  const targetDate = new Date(dateString);
  const currentTime = new Date();
  const differenceInMilliseconds = targetDate.getTime() - currentTime.getTime();
  const differenceInSeconds = Math.floor(differenceInMilliseconds / 1000);
  return Math.max(differenceInSeconds, 0);
};

export const imageUrlToBlob = async (
  imageUrl: string,
): Promise<Blob | null> => {
  try {
    const response = await fetch(imageUrl, { mode: "cors" });
    if (!response.ok) {
      console.error(`Failed to fetch image. Status: ${response.status}`);
      return null;
    }
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error("Error converting image URL to Blob:", error);
    return null;
  }
};

export const sortArray = <T extends Record<string, any>>(
  array: T[],
  key: keyof T,
): T[] => {
  if (!array) return [] as T[];
  return array.sort((a, b) => a[key].localeCompare(b[key]));
};

export function genFallbackId(prefix = "ao") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}
export function safeSet(k: string, v: any) {
  try {
    if (v !== null && v !== undefined) localStorage.setItem(k, String(v));
  } catch (e) {}
}
export function safeGet(k: string) {
  if (!k) return null;
  try {
    const valueFromStorage = localStorage.getItem(k);
    return valueFromStorage || null;
  } catch (e) {
    return null;
  }
}

export const dateToISOString = (date: string | number) => {
  if (!date) return "";
  return new Date(+date).toISOString();
};

export const isCampaignMode = () => {
  const params = new URL(window.location.href).searchParams;
  const foundParam = utmKeys.find((k) => !!params.get(k));
  return { isCampaignMode: !!foundParam, params };
};

export const returnConfigVariable = (
  runTimeKey: keyof typeof window.RUNTIME_CONFIG,
) => {
  return process.env[runTimeKey];
  // return window.RUNTIME_CONFIG &&
  //   !window.RUNTIME_CONFIG?.[runTimeKey].startsWith("$")
  //   ? window.RUNTIME_CONFIG?.[runTimeKey]
  //   : process.env[runTimeKey];
};

export const returnHyperKycConfig = (
  jwtToken: string,
  input_image: string = "",
) => {
  const hyperKycConfig = new HyperKycConfig(jwtToken, true);
  hyperKycConfig.setInputs({ input_image });
  return hyperKycConfig;
};

export function base64ToFile(base64: string, filename: string): File {
  const arr = base64.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

export function extractTextFromHtml(htmlString: string) {
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  return doc.querySelector("h1")?.textContent ?? "";
}

export function normalizeKeys<T = any, O = any>(
  obj: T | Array<T>,
): O | T | Array<O | T> {
  if (Array.isArray(obj)) {
    return obj.map(normalizeKeys);
  }

  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc: any, key: string) => {
      const normalizedKey = key.charAt(0).toLowerCase() + key.slice(1);

      acc[normalizedKey] = normalizeKeys(obj[key]);
      return acc;
    }, {});
  }

  return obj;
}

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;

    if (typeof data === "string") {
      return data;
    }

    if (data && typeof data === "object") {
      if ("message" in data) {
        const message = data.message;

        if (typeof message === "string") {
          return message;
        }

        if (Array.isArray(message)) {
          return message.join(", ");
        }
      }

      if ("error" in data && typeof data.error === "string") {
        return data.error;
      }
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unknown error occurred.";
}

export function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now(),
  });
}

export const removeEmptyFields = <T extends Record<string, any>>(obj: T) =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value != null && value !== ""),
  ) as Partial<T>;

export const addBase64ImagePrefix = (
  base64: string,
  mimeType = "image/png",
): string => {
  if (!base64) return "";
  if (base64.startsWith("data:")) return base64;
  return `data:${mimeType};base64,${base64}`;
};

export const isBase64File = (value: string): boolean => {
  if (!value) return false;
  if (value.startsWith("data:")) {
    return /^data:[^;]+;base64,[A-Za-z0-9+/]+={0,2}$/.test(value);
  }
  return /^[A-Za-z0-9+/]+={0,2}$/.test(value);
};
